import type { adminViews, timesheets as TS, workforce as WF } from '@cwos/api-client';

import { parseDayOrInstant } from '../../lib/timeFormat';

/**
 * Pure logic for the Time Clock area.
 *
 * Everything here is a function of data the API already returned. Nothing
 * fetches, nothing formats a number the server should have computed, and — the
 * rule that matters most on this surface — nothing invents certainty. A worker
 * whose last location arrived 40 minutes ago is reported as exactly that, never
 * as "here".
 */

// --- Time formatting --------------------------------------------------------

/** "8h 24m" — elapsed or recorded time, never a decimal a person has to convert. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** "8.25h" — the payroll-shaped form, used only where a total is being reconciled. */
export function formatHours(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  return `${(Math.max(0, minutes) / 60).toFixed(2)}h`;
}

/** "8:02 AM". */
export function formatClock(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}

/**
 * "Mon, Aug 10".
 *
 * Takes either a calendar day ("2026-08-10") or an instant. A bare day must NOT
 * go through `new Date(iso)` — that parses as UTC midnight and renders the
 * previous day for every viewer west of UTC. See parseDayOrInstant.
 */
export function formatDay(iso: string | null | undefined, locale?: string): string {
  if (!iso) return '—';
  const d = parseDayOrInstant(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Minutes between an instant and now — the basis of every freshness claim. */
export function minutesSince(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((now.getTime() - t) / 60_000));
}

// --- Location freshness -----------------------------------------------------

export type FreshnessLevel = 'fresh' | 'aging' | 'stale' | 'none';

export interface Freshness {
  level: FreshnessLevel;
  /** The sentence shown to a person. Always says WHEN, never "is here". */
  label: string;
  tone: 'good' | 'unsure' | 'look' | 'plain';
}

/**
 * How much a last-known location can be trusted, in words.
 *
 * The thresholds are deliberately conservative and the copy deliberately
 * past-tense. Phibian's position is "support, not surveillance": the product
 * reports the last thing a worker's device sent, and never asserts a live
 * position it cannot observe.
 */
export function locationFreshness(capturedAt: string | null | undefined, now: Date): Freshness {
  const mins = minutesSince(capturedAt, now);
  if (mins === null) {
    return {
      level: 'none',
      label: 'No location received',
      tone: 'plain',
    };
  }
  if (mins <= 10) {
    return {
      level: 'fresh',
      label: `Last location received ${mins <= 1 ? 'less than a minute' : `${mins} minutes`} ago`,
      tone: 'good',
    };
  }
  if (mins <= 45) {
    return {
      level: 'aging',
      label: `Last location received ${mins} minutes ago`,
      tone: 'unsure',
    };
  }
  const hours = Math.floor(mins / 60);
  return {
    level: 'stale',
    label:
      hours >= 1
        ? `Last location received ${hours} hour${hours === 1 ? '' : 's'} ago`
        : `Last location received ${mins} minutes ago`,
    tone: 'look',
  };
}

// --- Shift length -----------------------------------------------------------

export type ShiftLengthLevel = 'normal' | 'long' | 'overlong';

export interface ShiftLength {
  level: ShiftLengthLevel;
  label: string | null;
}

/**
 * Is this session running unusually long?
 *
 * Phrased as an observation about the RECORD, not about the person: a 13-hour
 * open session is far more often a forgotten clock-out than a 13-hour shift, and
 * the copy says so rather than implying either.
 */
export function shiftLength(elapsedMinutes: number | null): ShiftLength {
  if (elapsedMinutes === null) return { level: 'normal', label: null };
  if (elapsedMinutes >= 12 * 60) {
    return {
      level: 'overlong',
      label: 'Open more than 12 hours — likely a missing clock-out',
    };
  }
  if (elapsedMinutes >= 9 * 60) {
    return { level: 'long', label: 'Approaching a long shift' };
  }
  return { level: 'normal', label: null };
}

// --- Who's working ----------------------------------------------------------

export interface WorkingRow {
  workerId: string;
  name: string;
  roleTitle: string | null;
  programName: string | null;
  sessionId: string | null;
  clockedInAt: string | null;
  elapsedMinutes: number | null;
  freshness: Freshness;
  siteName: string | null;
  geofenceStatus: string | null;
  shiftLength: ShiftLength;
  /**
   * Whether the SOURCE of this row carries role, program and location at all.
   *
   * False for a row built from the workforce board, which has no such columns.
   * Without it `roleTitle: null` was rendered as "Not recorded" and
   * `[roleTitle, programName].filter(Boolean).join()` fell through to "No
   * program recorded" — two affirmative claims about a worker's record,
   * produced by a builder that hard-codes both fields to null because it never
   * fetched them. Every row on the board-only path made both claims.
   */
  detailsKnown: boolean;
}

/**
 * The same row, built from the workforce BOARD when the map is unavailable.
 *
 * `location_history:view` is granted to no role by default, so the common case
 * for an org_admin is having the board but not the map. The board knows who is
 * clocked in and for how long; it simply knows nothing about where. Deriving the
 * same row type from it keeps one rendering path and one interaction — the
 * location fields are honestly empty rather than the whole screen degrading to a
 * different, non-interactive list.
 */
export function whosWorkingFromBoard(
  board: adminViews.WorkforceBoard | undefined,
  now: Date,
): WorkingRow[] {
  if (!board) return [];
  const rows = board.activeSessions.map((s) => {
    const elapsed = s.durationMinutes ?? minutesSince(s.clockedInAt, now);
    return {
      workerId: s.workerId,
      name: s.displayName,
      roleTitle: null,
      programName: null,
      sessionId: s.sessionId,
      clockedInAt: s.clockedInAt,
      elapsedMinutes: elapsed,
      // No map means no position — stated as absent, never as fresh.
      freshness: locationFreshness(null, now),
      siteName: null,
      geofenceStatus: null,
      shiftLength: shiftLength(elapsed),
      // The board does not carry these columns. Saying so is different from
      // saying the worker has no role and no program.
      detailsKnown: false,
    } satisfies WorkingRow;
  });
  rows.sort((a, b) => (b.elapsedMinutes ?? 0) - (a.elapsedMinutes ?? 0));
  return rows;
}

/**
 * Turns the workforce map into the "who is working right now" list.
 *
 * Only workers with an OPEN session appear — a closed session is a fact about
 * earlier today, and putting it in a "currently working" list is the exact kind
 * of quiet inaccuracy this surface exists to remove.
 */
export function whosWorking(map: WF.WorkforceMap | undefined, now: Date): WorkingRow[] {
  if (!map) return [];
  const rows: WorkingRow[] = [];
  for (const worker of map.workers) {
    if (!worker.hasOpenSession) continue;
    const open = worker.sessions.find((s) => s.openNow) ?? null;
    const elapsed = open ? minutesSince(open.clockedInAt, now) : null;
    rows.push({
      workerId: worker.workerId,
      name: worker.displayName,
      roleTitle: worker.roleTitle,
      programName: worker.programName,
      sessionId: open?.sessionId ?? null,
      clockedInAt: open?.clockedInAt ?? null,
      elapsedMinutes: elapsed,
      freshness: locationFreshness(worker.lastLocation?.capturedAt, now),
      siteName: worker.lastLocation?.siteName ?? null,
      geofenceStatus: worker.lastLocation?.geofenceStatus ?? null,
      shiftLength: shiftLength(elapsed),
      detailsKnown: true,
    });
  }
  // Longest-running first: a 13-hour open session is the row that needs a human.
  rows.sort((a, b) => (b.elapsedMinutes ?? 0) - (a.elapsedMinutes ?? 0));
  return rows;
}

// --- Overview ---------------------------------------------------------------

export interface TimeOverview {
  clockedIn: number;
  missingClockOut: number;
  notClockedIn: number;
  totalMinutesToday: number;
  longShifts: number;
  staleLocations: number;
}

/**
 * The Overview numbers, each derived from a list the screen can also show.
 *
 * Every count here is the length of something drillable. A metric whose
 * underlying rows cannot be produced is a dead card, and this surface has none.
 */
/**
 * Whose figures the "Right now" card is showing, or null when they are the
 * organisation's.
 *
 * The four KPIs are counts over whatever the reader reaches. Until the API said
 * which that was, a supervisor's card reported their own team's three open
 * sessions in the same words, under the same heading, as an administrator's
 * card reported the organisation's forty. Every count was correct and the card
 * was not.
 *
 * Null rather than "across the organisation" for the org-wide case, so the
 * default screen is unchanged and the sentence appears only when it is telling
 * the reader something they could otherwise get wrong.
 */
export function overviewScopeNote(board: adminViews.WorkforceBoard | undefined): string | null {
  return board?.scope === 'caseload'
    ? 'These are the workers assigned to you. Seeing the whole organisation needs an organisation-wide permission.'
    : null;
}

/**
 * The frame for the check-in verification figures, when they are narrower than
 * the organisation.
 *
 * The summary itself names nobody — it is eight integers — so it is not the
 * disclosure the board is. It is framed for a different reason: it sits directly
 * beside the board and the exception queue, both of which now show the reader's
 * own team, and a total counted over the organisation above panels counted over
 * three people is a figure the reader cannot account for. The first thing a
 * supervisor would do with an unframed "90 unverified" is conclude something
 * about their own team.
 *
 * Null for the org-wide case, so the default screen is unchanged and the
 * sentence appears only when it is telling the reader something they could
 * otherwise get wrong — the same rule `overviewScopeNote` follows.
 */
export function verificationScopeNote(
  summary: adminViews.VerificationSummary | undefined,
): string | null {
  return summary?.scope === 'caseload'
    ? 'These check-ins are the ones recorded by the workers assigned to you, not the whole organisation.'
    : null;
}

/**
 * The frame for the exception queue, when it is narrower than the organisation.
 *
 * "No open exceptions" is a sentence about the organisation unless something
 * says otherwise, and a supervisor reading it about their own three people
 * would draw the wrong conclusion about the other forty.
 */
export function exceptionScopeNote(panel: adminViews.ExceptionPanel | undefined): string | null {
  return panel?.scope === 'caseload'
    ? 'These are the exceptions raised for the workers assigned to you. Seeing the whole organisation needs an organisation-wide permission.'
    : null;
}

export function overviewFrom(
  board: adminViews.WorkforceBoard | undefined,
  working: readonly WorkingRow[],
): TimeOverview {
  const totalMinutesToday = (board?.activeSessions ?? []).reduce(
    (sum, s) => sum + (s.durationMinutes ?? 0),
    0,
  );
  return {
    clockedIn: board?.currentlyClockedIn ?? working.length,
    missingClockOut: board?.missingClockOuts.length ?? 0,
    notClockedIn: board?.unscheduledNoShows.length ?? 0,
    totalMinutesToday,
    longShifts: working.filter((w) => w.shiftLength.level !== 'normal').length,
    staleLocations: working.filter(
      (w) => w.freshness.level === 'stale' || w.freshness.level === 'none',
    ).length,
  };
}

// --- Timesheets -------------------------------------------------------------

export type TimesheetFilter = 'all' | 'needs_approval' | 'open' | 'approved' | 'returned';

export const TIMESHEET_FILTER_LABELS: Record<TimesheetFilter, string> = {
  all: 'All timesheets',
  needs_approval: 'Waiting on approval',
  open: 'Still open',
  approved: 'Approved',
  returned: 'Returned for correction',
};

export function matchesTimesheetFilter(
  period: TS.TimesheetPeriod,
  filter: TimesheetFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'needs_approval':
      return period.status === 'submitted';
    case 'open':
      return period.status === 'open' || period.status === 'reopened';
    case 'approved':
      return period.status === 'approved';
    case 'returned':
      return period.status === 'returned';
  }
}

export interface StatusPresentation {
  label: string;
  tone: 'good' | 'look' | 'unsure' | 'urgent' | 'plain';
}

/**
 * A timesheet status, in the words a reviewer uses.
 *
 * "Reopened" is the one that matters: it means an approved week was changed by a
 * later correction, and calling it "open" would hide that an approval was
 * superseded.
 */
export function timesheetStatus(status: string): StatusPresentation {
  switch (status) {
    case 'approved':
      return { label: 'Approved', tone: 'good' };
    case 'submitted':
      return { label: 'Waiting on approval', tone: 'look' };
    case 'returned':
      return { label: 'Returned for correction', tone: 'unsure' };
    case 'reopened':
      return { label: 'Reopened by a correction', tone: 'unsure' };
    case 'open':
      return { label: 'Open', tone: 'plain' };
    default:
      return { label: status, tone: 'plain' };
  }
}

/** The week-level exceptions, in business language with a next step. */
export const EXCEPTION_COPY: Record<
  TS.TimesheetExceptionFlag,
  { label: string; meaning: string; tone: StatusPresentation['tone'] }
> = {
  location_exception: {
    label: 'Location exception',
    meaning:
      'A punch this week could not be verified at a work site and its exception has not been decided. The time itself is recorded — review the exception under Settings → Location → Exceptions.',
    tone: 'look',
  },
  missing_clock_out: {
    label: 'Missing clock-out',
    meaning: 'A session was never closed, so its hours are unknown rather than zero.',
    tone: 'urgent',
  },
  auto_closed: {
    label: 'Closed automatically',
    meaning: 'Phibian closed this session because nobody did. The end time is an estimate.',
    tone: 'look',
  },
  open_session: {
    label: 'Still clocked in',
    meaning: 'This week has a session that is still running.',
    tone: 'unsure',
  },
  adjustment_pending: {
    label: 'Correction waiting',
    meaning: 'Someone asked to change this week and no one has decided yet.',
    tone: 'look',
  },
};

/**
 * Groups a week's sessions into days.
 *
 * The day total is the SUM OF ITS SESSIONS, computed here from the same rows the
 * screen lists, so a day heading can never disagree with the rows beneath it.
 */
export interface DayGroup {
  dayIso: string;
  rows: TS.AggregateRow[];
  minutes: number;
  firstIn: string | null;
  lastOut: string | null;
  /** True when a session in this day never closed — the day total is a floor. */
  incomplete: boolean;
}

export function groupWeekByDay(aggregate: TS.WeeklyAggregate | undefined): DayGroup[] {
  if (!aggregate) return [];
  const byDay = new Map<string, TS.AggregateRow[]>();
  for (const row of aggregate.rows) {
    const key = row.clockedInAt.slice(0, 10);
    const list = byDay.get(key);
    if (list) list.push(row);
    else byDay.set(key, [row]);
  }
  const days: DayGroup[] = [];
  for (const [dayIso, rows] of byDay) {
    const sorted = [...rows].sort((a, b) => a.clockedInAt.localeCompare(b.clockedInAt));
    const closed = sorted.filter((r) => r.clockedOutAt !== null);
    days.push({
      dayIso,
      rows: sorted,
      minutes: sorted.reduce((sum, r) => sum + (r.minutes ?? 0), 0),
      firstIn: sorted[0]?.clockedInAt ?? null,
      lastOut: closed.length > 0 ? closed[closed.length - 1]!.clockedOutAt : null,
      incomplete: sorted.some((r) => r.clockedOutAt === null),
    });
  }
  days.sort((a, b) => a.dayIso.localeCompare(b.dayIso));
  return days;
}

/**
 * Does the week total match the sum of its days?
 *
 * A reconciliation check rather than a display helper: if these ever disagree,
 * the screen says so instead of silently showing two different truths.
 */
export function weekReconciles(aggregate: TS.WeeklyAggregate | undefined): boolean {
  if (!aggregate) return true;
  const summed = aggregate.rows.reduce((sum, r) => sum + (r.minutes ?? 0), 0);
  return summed === aggregate.totalMinutes;
}

/**
 * ONE OPEN SESSION AN APPROVER IS BEING ASKED ABOUT.
 *
 * `sessionId` always. `clockedInAt` is null when the week this screen loaded
 * does not contain the session the SERVER just named — see
 * {@link describeOpenSessions} for why that is a state and not an error.
 */
export interface OpenSessionSubject {
  sessionId: string;
  clockedInAt: string | null;
  /** True when the loaded week has no row for this id. */
  unknownToThisScreen: boolean;
  /** True when the server said this one had not been acknowledged yet. */
  newSinceLastAttempt: boolean;
}

/**
 * The open sessions a refused approval named, joined to what this screen knows
 * about them.
 *
 * THE SERVER'S LIST IS THE LIST. It is derived inside the approval transaction
 * under the period's row lock; the aggregate this screen is rendering was
 * fetched earlier and can be older. So the ids drive the output and the rows
 * only decorate it — and an id the rows do not carry is reported AS an id, with
 * `unknownToThisScreen`, rather than dropped. Dropping it would shorten the
 * list of things the approver is accepting responsibility for, which is the one
 * direction this must never fail in: they would be told they are acknowledging
 * two sessions and actually be acknowledging three.
 *
 * `newSinceLastAttempt` marks the sessions the server reported as
 * unacknowledged. On a first refusal that is all of them; on a second it is
 * exactly the ones that opened while the approver was reading, which is a
 * different sentence and has to be available as one.
 *
 * Order follows the server's, so the list a person reads and the list sent back
 * as the acknowledgement cannot disagree about what came first.
 */
export function describeOpenSessions(
  conflict: { openSessionIds: readonly string[]; unacknowledgedSessionIds: readonly string[] },
  rows: readonly TS.AggregateRow[] | undefined,
): OpenSessionSubject[] {
  const byId = new Map((rows ?? []).map((r) => [r.sessionId, r] as const));
  const unacked = new Set(conflict.unacknowledgedSessionIds);
  return conflict.openSessionIds.map((sessionId) => {
    const row = byId.get(sessionId);
    return {
      sessionId,
      clockedInAt: row?.clockedInAt ?? null,
      unknownToThisScreen: row === undefined,
      newSinceLastAttempt: unacked.has(sessionId),
    };
  });
}

/**
 * A period's start as an INSTANT the API will accept.
 *
 * `periodStart` is a DATE (`2026-08-03`); the week endpoints validate an ISO
 * datetime with an offset and reject a bare date with a 400. Midday UTC is used
 * rather than midnight so the instant still lands inside the intended week for
 * any reasonable organization timezone — the server anchors it to that week's
 * Monday either way.
 */
export function weekOfInstant(periodStart: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(periodStart)) {
    return new Date(`${periodStart}T12:00:00.000Z`).toISOString();
  }
  const d = new Date(periodStart);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

// --- Verification -----------------------------------------------------------

/**
 * The two buckets the verification tally rolls up to.
 *
 * `verified_inside` is the only state that means "at a work site". Everything
 * else — outside, no geofence, low accuracy, no fix, permission denied, policy
 * off, worker-attested — is NOT a finding of wrongdoing, which is why the second
 * bucket is called "needs a look" and not "away".
 */
export const AT_SITE_STATES = new Set(['verified_inside']);

export const UNVERIFIED_STATES = new Set([
  'verified_outside',
  'recorded_no_geofence',
  'low_accuracy',
  'unavailable_no_fix',
  'unavailable_permission_denied',
  'unavailable_policy_off',
  'attested_by_worker',
]);

export interface VerificationSplit {
  total: number;
  atSite: number;
  needsLook: number;
  unknown: number;
}

export function splitVerification(
  summary: adminViews.VerificationSummary | undefined,
): VerificationSplit {
  const split: VerificationSplit = { total: 0, atSite: 0, needsLook: 0, unknown: 0 };
  if (!summary) return split;
  split.total = summary.totalPunches;
  for (const s of summary.byState) {
    if (AT_SITE_STATES.has(s.state)) split.atSite += s.count;
    else if (UNVERIFIED_STATES.has(s.state)) split.needsLook += s.count;
    else split.unknown += s.count;
  }
  return split;
}

/** "0.4 mi" / "120 ft" — distance from the expected site, in units people use. */
export function formatDistance(meters: number | null | undefined): string {
  if (meters === null || meters === undefined || !Number.isFinite(meters)) return '—';
  const feet = meters * 3.280_84;
  if (feet < 1000) return `${Math.round(feet)} ft`;
  return `${(meters / 1609.344).toFixed(1)} mi`;
}
